import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonical,
  ogImage,
  ogType = 'website',
  robots = 'index, follow'
}) => {
  const { currentLanguage, getPathWithLanguage, getPathWithoutLanguage, availableLanguages } = useLanguage();
  const { t } = useTranslation();

  const baseUrl = window.location.origin.replace('://www.', '://');
  const currentPath = window.location.pathname;
  const pathWithoutLang = getPathWithoutLanguage(currentPath) || '/';
  const languageMeta = {
    ba: { html: 'bs-BA', hreflang: 'bs-BA', locale: 'bs_BA' },
    en: { html: 'en', hreflang: 'en', locale: 'en_US' },
    fr: { html: 'fr', hreflang: 'fr', locale: 'fr_FR' },
    es: { html: 'es', hreflang: 'es', locale: 'es_ES' },
    it: { html: 'it', hreflang: 'it', locale: 'it_IT' },
    ru: { html: 'ru', hreflang: 'ru', locale: 'ru_RU' },
    ar: { html: 'ar', hreflang: 'ar', locale: 'ar_AR' },
    de: { html: 'de', hreflang: 'de', locale: 'de_DE' },
    pt: { html: 'pt', hreflang: 'pt', locale: 'pt_PT' },
    tr: { html: 'tr', hreflang: 'tr', locale: 'tr_TR' },
    zh: { html: 'zh', hreflang: 'zh', locale: 'zh_CN' }
  };

  const canonicalUrl = canonical || `${baseUrl}${getPathWithLanguage(pathWithoutLang, currentLanguage)}`;
  const canonicalSearch = canonicalUrl.indexOf('?') > -1 ? canonicalUrl.substring(canonicalUrl.indexOf('?')) : '';

  // Generate hreflang URLs
  const hreflangs = availableLanguages
    .filter((lang) => !['hi', 'ur'].includes(lang))
    .map((lang) => ({
      lang: languageMeta[lang] ? languageMeta[lang].hreflang : lang,
      url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, lang)}${canonicalSearch}`
    }))
    .concat([{ lang: 'x-default', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'ba')}${canonicalSearch}` }]);
  let finalTitle = title || t('seo.homeTitle') || 'OPK.ba - Online Shopping Platform';
  if ( finalTitle == 'seo.homeTitle' ) { finalTitle = 'OPK.ba - Online Shopping Platform'; }
  
  const finalDescription = description || t('seo.homeDescription');
  const finalKeywords = keywords || t('seo.homeKeywords');
  const currentMeta = languageMeta[currentLanguage] || languageMeta.ba;
  const alternateLocales = Object.keys(languageMeta)
    .filter((lang) => lang !== currentLanguage)
    .map((lang) => languageMeta[lang].locale);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentMeta.html} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={robots} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags */}
      {hreflangs.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="OPK.ba" />
      <meta property="og:locale" content={currentMeta.locale} />
      {alternateLocales.map((locale) => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Language and Region */}
      <meta name="language" content={currentMeta.html} />
      <meta name="geo.region" content="BA" />
      <meta name="geo.country" content="Bosnia and Herzegovina" />
    </Helmet>
  );
};

export default SEOHead;
